import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminEventDto, EventCreateInput, EventUpdateInput } from '@upme/contracts';
import { api } from '../../app/api-client';
import { eventByIdOptions, eventsKey } from './queries';

function invalidateList(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: [...eventsKey, 'list'] });
}

/** Закрепить/снять менеджера события (#166). null — снять. */
export function useSetEventManager(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (managerUserId: string | null) =>
      api.patch(`admin/events/${id}/manager`, { json: { managerUserId } }).json<AdminEventDto>(),
    onSuccess: (data) => {
      qc.setQueryData(eventByIdOptions(id).queryKey, data);
      return invalidateList(qc);
    },
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EventCreateInput) => api.post('admin/events', { json: body }).json<AdminEventDto>(),
    onSuccess: () => invalidateList(qc),
  });
}

export function useUpdateEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EventUpdateInput) =>
      api.patch(`admin/events/${id}`, { json: body }).json<AdminEventDto>(),
    onSuccess: (data) => {
      qc.setQueryData(eventByIdOptions(id).queryKey, data);
      return invalidateList(qc);
    },
  });
}

function useStatusMutation(id: string, action: 'publish' | 'archive' | 'restore') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`admin/events/${id}/${action}`).json<AdminEventDto>(),
    onSuccess: (data) => {
      qc.setQueryData(eventByIdOptions(id).queryKey, data);
      return invalidateList(qc);
    },
  });
}

export const usePublishEvent = (id: string) => useStatusMutation(id, 'publish');
export const useArchiveEvent = (id: string) => useStatusMutation(id, 'archive');
export const useRestoreEvent = (id: string) => useStatusMutation(id, 'restore');

export function useApproveEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`admin/events/${id}/approve`).json<AdminEventDto>(),
    onSuccess: (data) => {
      qc.setQueryData(eventByIdOptions(id).queryKey, data);
      return invalidateList(qc);
    },
  });
}

export function useRejectEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post(`admin/events/${id}/reject`, { json: { reason } }).json<AdminEventDto>(),
    onSuccess: (data) => {
      qc.setQueryData(eventByIdOptions(id).queryKey, data);
      return invalidateList(qc);
    },
  });
}

export function useConvertEventToNews(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`admin/events/${id}/convert-to-news`).json<{ newsId: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventByIdOptions(id).queryKey });
      return invalidateList(qc);
    },
  });
}

export function useHardDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/events/${id}?hard=true`),
    onSuccess: () => invalidateList(qc),
  });
}
